(() => {
  if (window.__lafloreCartThresholdLoaded) return;
  window.__lafloreCartThresholdLoaded = true;

  const milestones = [{ subtotal: 250, rate: 20 }, { subtotal: 550, rate: 30 }, { subtotal: 850, rate: 40 }];
  const excludedSkus = new Set(['JOB01BLK', 'JOB02EMR']);
  const currency = () => window.Shopify?.currency?.active || 'USD';
  const root = () => window.Shopify?.routes?.root || '/';
  const money = (cents, whole = false) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: currency(), minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }).format(whole ? Math.ceil(cents / 100) : cents / 100);
  let timer;
  let revision = 0;
  let subscribed = false;
  let hasResult = false;
  let hasItems = false;
  let lastCents = 0;
  let showingIntro = true;

  async function productFor(handle, productCache) {
    if (!productCache.has(handle)) {
      const request = fetch(`${root()}products/${encodeURIComponent(handle)}?view=threshold-eligibility`, { credentials: 'same-origin', cache: 'no-store' })
        .then(response => { if (!response.ok) throw new Error('Product unavailable'); return response.json(); });
      productCache.set(handle, request);
    }
    return productCache.get(handle);
  }

  async function eligibleCents(cart) {
    const productCache = new Map();
    const values = await Promise.all(cart.items.map(async item => {
      if (excludedSkus.has((item.sku || '').toUpperCase())) return 0;
      try {
        const product = await productFor(item.handle, productCache);
        const variant = product.variants.find(candidate => String(candidate.id) === String(item.variant_id));
        if (!variant || (variant.sku && excludedSkus.has(variant.sku.toUpperCase()))) return 0;
        if (variant.promotional || (variant.compare_at_price && variant.compare_at_price > variant.price)) return 0;
        const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
        if (tags.some(tag => String(tag).trim().toLowerCase() === 'cart-threshold-exclude')) return 0;
        const price = Number(variant.price);
        const quantity = Number(item.quantity);
        return Number.isFinite(price) && price >= 0 && Number.isInteger(quantity) && quantity > 0 ? price * quantity : 0;
      } catch (error) {
        return 0;
      }
    }));
    return values.reduce((sum, value) => sum + value, 0);
  }

  function updateBar(bar, cents) {
    const dollars = cents / 100;
    const unlocked = milestones.filter(tier => dollars >= tier.subtotal);
    const current = unlocked.length ? unlocked[unlocked.length - 1].rate : 0;
    const next = milestones.find(tier => tier.subtotal > dollars);
    const progress = Math.max(0, Math.min(100, (dollars / milestones[milestones.length - 1].subtotal) * 100));
    const format = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`__${key}__`, value), template);
    let message;
    if (next) {
      const amount = money(Math.round((next.subtotal - dollars) * 100), true);
      message = format(cents === 0 ? bar.dataset.empty : bar.dataset.remaining, { amount, rate: next.rate });
    } else {
      message = format(bar.dataset.unlocked, { rate: current });
    }
    const messageElement = bar.querySelector('.cart-threshold__message');
    messageElement.replaceChildren();
    if (showingIntro && bar.dataset.placement === 'announcement') {
      messageElement.append(document.createTextNode(bar.dataset.introPrefix));
      const offer = document.createElement('span');
      offer.className = 'cart-threshold__unlocked';
      offer.textContent = bar.dataset.introOffer;
      messageElement.append(offer);
    } else if (current > 0) {
      const unlockedLabel = document.createElement('span');
      unlockedLabel.className = 'cart-threshold__unlocked';
      unlockedLabel.textContent = format(bar.dataset.unlocked, { rate: current });
      messageElement.append(unlockedLabel);
      if (next) messageElement.append(document.createTextNode(` · ${message}`));
    } else {
      messageElement.textContent = message;
    }
    const track = bar.querySelector('.cart-threshold__track');
    track.setAttribute('aria-valuenow', String(Math.round(progress)));
    bar.querySelector('.cart-threshold__fill').style.width = `${progress}%`;
    bar.dataset.ready = "true";
    bar.hidden = false;
  }

  async function refresh(suppliedCart) {
    const currentRevision = ++revision;
    try {
      let cart = suppliedCart;
      if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') {
        const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Cart unavailable');
        cart = await response.json();
      }
      hasItems = cart.items.length > 0;
      const cents = await eligibleCents(cart);
      if (currentRevision !== revision) return;
      hasResult = true;
      lastCents = cents;
      document.querySelectorAll('cart-threshold-progress').forEach(bar => updateBar(bar, cents));
    } catch (error) {
      if (currentRevision !== revision) return;
      hasResult = false;
      document.querySelectorAll('cart-threshold-progress').forEach(bar => {
        bar.querySelector('.cart-threshold__message').textContent = showingIntro && bar.dataset.placement === 'announcement' ? bar.dataset.intro : bar.dataset.fallback;
        bar.querySelector('.cart-threshold__track').setAttribute('aria-valuenow', '0');
        bar.querySelector('.cart-threshold__fill').style.width = '0%';
        bar.hidden = false;
      });
    }
  }

  function cartUpdated(event) {
    clearTimeout(timer);
    timer = null;
    refresh(event?.detail?.cart || event?.cart);
  }
  function subscribe() {
    if (subscribed || !window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) return;
    subscribed = true;
    window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, cartUpdated);
  }
  function schedule() {
    if (timer) return;
    timer = setTimeout(() => { timer = null; refresh(); }, 100);
  }
  document.addEventListener('DOMContentLoaded', () => { subscribe(); refresh(); });
  window.addEventListener('load', subscribe, { once: true });
  document.addEventListener('cart:updated', cartUpdated);
  document.addEventListener('cart:change', cartUpdated);
  document.addEventListener('cart:refresh', () => refresh());
  window.addEventListener('focus', () => refresh());
  setInterval(() => { if (!document.hidden && hasItems) refresh(); }, 30000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
  new MutationObserver(records => {
    const addedBar = records.some(record => Array.from(record.addedNodes).some(node =>
      node.nodeType === 1 && (node.matches('cart-threshold-progress') || node.querySelector('cart-threshold-progress'))
    ));
    if (!addedBar) return;
    if (hasResult) document.querySelectorAll('cart-threshold-progress:not([data-ready])').forEach(bar => updateBar(bar, lastCents));
    schedule();
  }).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-threshold-cart-link]');
    if (!link) return;
    const trigger = document.querySelector('a.cart-drawer-button[aria-controls="CartDrawer"]');
    if (trigger && document.getElementById('CartDrawer')) {
      event.preventDefault();
      trigger.click();
    }
  });
  let rotating = false;
  async function rotateAnnouncement(manual = false) {
    if (rotating || document.hidden || !hasResult) return;
    const bars = Array.from(document.querySelectorAll('cart-threshold-progress[data-placement="announcement"]'));
    if (!bars.length || (!manual && bars.some(bar => bar.matches(':hover, :focus-within')))) return;
    rotating = true;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const messages = bars.map(bar => bar.querySelector('.cart-threshold__message'));
    if (!reducedMotion) {
      await Promise.all(messages.map(message => message.animate(
        [{ transform: 'translateX(0)', opacity: 1 }, { transform: 'translateX(36px)', opacity: 0 }],
        { duration: 250, easing: 'ease-in', fill: 'forwards' }
      ).finished.catch(() => {})));
    }
    showingIntro = !showingIntro;
    bars.forEach(bar => updateBar(bar, lastCents));
    messages.forEach(message => {
      message.getAnimations().forEach(animation => animation.cancel());
      if (!reducedMotion) message.animate(
        [{ transform: 'translateX(-36px)', opacity: 0 }, { transform: 'translateX(0)', opacity: 1 }],
        { duration: 350, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
    });
    rotating = false;
  }
  let rotationTimer = setInterval(() => rotateAnnouncement(), 4500);
  document.addEventListener('click', event => {
    const arrow = event.target.closest('[data-threshold-arrow]');
    if (!arrow) return;
    event.preventDefault();
    rotateAnnouncement(true);
    clearInterval(rotationTimer);
    rotationTimer = setInterval(() => rotateAnnouncement(), 4500);
  });
  subscribe();
  refresh();
})();

